import { Avatar, Stack, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';
import { Iconify } from '@sdf/base';
import { useEffect, useMemo } from 'react';
import {
  BrollRenderStatus,
  CardTypeEnum,
  MediaStatus,
  MediaStatusType,
  STATUS_REGISTRATION_REQUIREMENT,
} from '../../utils';
import { AvatarBox, FooterBox, StyledCard } from './AvatarSection';
import { DropDownOptions } from './DropDownOptions';
import { DraftStatus, Status } from '../../../t2c-fe/@types';
import { ProgressStuckNote, StatusWithProgressBar } from '../../Common';
import { useAppContext } from '../../hooks';

interface AvatarCardProps {
  avatarName: string;
  avatarLogo: string;
  handleClick: () => void;
  status?: MediaStatusType;
  type?: CardTypeEnum;
  message?: string;
  date: string;
  time: string;
  speechContentId?: string | null;
  creationId?: string;
  url?: string;
  isBrollCaptionVideo?: boolean;
  isAvatarCreation?: boolean;
  isPublic?: boolean;
  handleBrollCaptionVideoPreview?: Function;
  brollRenderStatus?: BrollRenderStatus;
  refetchData?: Function;
  isImage?: boolean;
  draftStatus?: DraftStatus;
  draftMessage?: string;
  isActive?: boolean;
}

interface StyledStatusBoxProps {
  isCompleted?: boolean;
  isError?: boolean;
  isBrollRendered?: boolean;
}

const StyledStatusBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isCompleted' && prop !== 'isError' && prop !== 'isBrollRendered',
})<StyledStatusBoxProps>(({ theme, isCompleted, isError, isBrollRendered }) => ({
  background:
    isCompleted || isError || isBrollRendered
      ? theme.palette.background.card
      : theme.palette.gradients.gradient2,
  position: 'absolute',
  zIndex: 99,
  borderRadius: isCompleted ? '100px' : '80px',
  padding: isCompleted ? '10px' : '0px 10px 0 10px',
  backgroundColor: isBrollRendered ? 'green' : isError ? theme.palette.error.main : 'inherit',
  marginTop: isCompleted ? '' : '110px',
  width: isCompleted ? '' : '45%',
  textAlign: 'center',
  borderColor: isError ? theme.palette.text.error : '',
  borderWidth: isError ? '1px' : '',
}));

// const StatusBar = ({
//   status,
//   type,
//   isImage,
//   draftStatus,
// }: {
//   status: MediaStatusType;
//   type?: CardTypeEnum;
//   isImage?: boolean;
//   draftStatus?: DraftStatus;
// }) => {
//   const isCompleted = useMemo(() => status === 'Success', [status]);
//   const isError = useMemo(() => status === 'Error', [status]);

//   const notStarted = status === MediaStatus.NotStarted;
//   const inDraft = draftStatus === DraftStatus.InProgress;

//   if ((isCompleted || inDraft) && !isImage)
//     return (
//       <StyledStatusBox isCompleted={isCompleted || inDraft}>
//         <Iconify icon={'solar:play-bold'} width={'1rem'} height={'1rem'} color={(theme)=> theme.palette.background.activeBorder} />
//       </StyledStatusBox>
//     );

//   if (isCompleted || (type === CardTypeEnum.LipsyncVideo && notStarted && inDraft)) return null;

//   return (
//     <StyledStatusBox isError={isError}>
//       <Typography variant="body2" letterSpacing={'0.5px'} sx={{ textWrap: 'nowrap' }}>
//         {isError ? 'Failed' : 'In Progress...'}
//       </Typography>
//     </StyledStatusBox>
//   );
// };

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip arrow {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
}));

export const AvatarCard = ({
  avatarName,
  avatarLogo,
  handleClick,
  status,
  message,
  type,
  date,
  time,
  creationId,
  url,
  isBrollCaptionVideo,
  isAvatarCreation,
  isPublic,
  handleBrollCaptionVideoPreview,
  brollRenderStatus,
  refetchData,
  isImage,
  draftStatus,
  draftMessage,
  isActive = false,
}: AvatarCardProps) => {
  const isAvatarVideo = type === CardTypeEnum.LipsyncVideo;

  const {
    websocket: { isConnected, sendMessage, avatarCreationTaskMap, avatarVideoTaskMap },
  } = useAppContext();

  const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

  const avatarCreationTask = avatarCreationTaskMap.get(creationId);
  const avatarVideoTask = avatarVideoTaskMap.get(creationId);

  const videoDataStatus =
    (isAvatarVideo ? avatarVideoTask?.status : avatarCreationTask?.status) || status;

  const hideDropdownButton = registrationRequired.includes(videoDataStatus);

  const cardProgress = avatarVideoTask?.progress || avatarCreationTask?.progress || 0;

  useEffect(() => {
    if (avatarVideoTask?.status === Status.Success && avatarVideoTask?.progress === 100) {
      refetchData && refetchData();
    }
  }, [avatarVideoTask?.status]);

  useEffect(() => {
    const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

    if (isConnected && registrationRequired.includes(videoDataStatus)) {
      sendMessage({
        event: 'register',
        data: {
          id: creationId,
        },
      });
    }
  }, [isConnected, status, creationId]);

  return (
    <StyledCard variant="outlined" onClick={handleClick} status={videoDataStatus as MediaStatus} isActive={isActive}>
      <AvatarBox height={'85%'} status={status} className="avatar-box">
        <Avatar
          alt={avatarName}
          src={avatarLogo || avatarCreationTask?.data?.data?.thumbnail}
          sx={{ width: '152px', height: '152px' }}
        >
          <Box
            sx={{
              width: '152px',
              height: '152px',
              background: (theme) => theme.palette.background.primary,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Iconify
              icon={'ooui:user-avatar'}
              width={'24px'}
              height={'24px'}
              mr={0.5}
              color={(theme) => theme.palette.text.primary}
            />
          </Box>
        </Avatar>
        <Stack
          direction="row"
          gap={0.5}
          sx={{
            position: 'absolute',
            top: '10px',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {videoDataStatus === Status.Error ? (
            <Tooltip title={message || 'Something went wrong during Face Detection'}>
              <Box
                sx={{
                  background: (theme) => theme.palette.gradients.gradient8,
                  padding: '0 12px',
                  borderTopRightRadius: '20px',
                  borderBottomRightRadius: '20px',
                  fontSize: '14px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Iconify
                  icon={'heroicons-outline:exclamation'}
                  width={'20px'}
                  height={'20px'}
                  mr={0.5}
                  color={(theme) => theme.palette.text.primary}
                />
                <span>Failed </span>
              </Box>
            </Tooltip>
          ) : videoDataStatus === Status.NotStarted ? (
            <Box
              sx={{
                background: (theme) => theme.palette.gradients.gradient9,
                padding: '2px 12px',
                borderTopRightRadius: '20px',
                borderBottomRightRadius: '20px',
                fontSize: '14px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <span>{draftMessage || 'Draft'} </span>
            </Box>
          ) : (
            videoDataStatus !== Status.Success && (
              <Box
                sx={{
                  padding: '0 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ProgressStuckNote showToolTip={true} />
              </Box>
            )
          )}
          <div></div>
          {!hideDropdownButton && (!isPublic) && (isAvatarVideo || videoDataStatus === Status.Error || videoDataStatus === Status.Success) && (
            <DropDownOptions
              id={creationId}
              name={avatarName}
              url={url}
              isBrollCaptionVideo={isBrollCaptionVideo}
              isAvatarCreation={isAvatarCreation}
              handleBrollCaptionVideoPreview={handleBrollCaptionVideoPreview}
              brollRenderStatus={brollRenderStatus}
              refetchData={refetchData}
              status={status}
              type={type}
            />
          )}
        </Stack>
        {!isImage && videoDataStatus === Status.Success && (
          <StyledStatusBox
            isCompleted={true}
            sx={{
              position: 'absolute',
              transition: 'transform 0.1s ease-out',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: (theme) => theme.shadows[4],
              },
            }}
          >
            <Iconify
              icon={'solar:play-bold'}
              width={'1rem'}
              height={'1rem'}
              color={(theme) => theme.palette.background.activeBorder}
            />
          </StyledStatusBox>
        )}
        {draftStatus !== DraftStatus.InProgress &&
          (hideDropdownButton || videoDataStatus === Status.Error) && (
            <Stack
              sx={{
                minWidth: 150,
                width: '40%',
                position: 'absolute',
                top: '65%',
              }}
            >
              <StatusWithProgressBar
                progressPercentage={cardProgress}
                status={videoDataStatus}
                isBrollCaption
              />
            </Stack>
          )}
      </AvatarBox>
      <FooterBox status={status} className="avatar-footer">
        <Tooltip title={avatarName}>
          <Typography
            sx={{
              display: 'inline-block',
              maxWidth: '50%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            className="avatar-name"
            fontWeight={400}
            fontSize={14}
          >
            {avatarName ?? '--'}
          </Typography>
        </Tooltip>

        <Typography
          className="avatar-time"
          variant="caption"
          sx={{
            fontSize: 12,
          }}
        >
          {date}
          &nbsp;
          {time}
        </Typography>
      </FooterBox>
    </StyledCard>
  );
};
